import { useMemo, useState, type FormEvent } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TASK_CATEGORIES,
  groupTasks,
  taskStats,
  type Task,
  type TaskBucket,
  type TaskCategory,
  type Wedding,
} from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { FilterPill } from '@/components/ui/FilterPill';
import { Metric, MetricFootnote } from '@/components/ui/Stat';
import { TextInput } from '@/components/ui/Field';
import { Suggestions } from './Suggestions';
import { WeeklyReminderToggle } from './WeeklyReminderToggle';
import {
  useCreateTask,
  useDeleteTask,
  useGenerateChecklist,
  useRescheduleChecklist,
  useTasks,
  useUpdateTask,
} from './queries';

/**
 * The planning checklist.
 *
 * The question it answers is **what is on us now**, so the list is grouped by
 * how soon something is due rather than by how long before the wedding it sits
 * in the template. "Nine to twelve months out" is a fact about the template;
 * "this month" is a fact about you.
 *
 * Buckets other than the near ones start collapsed. Forty-three open rows is the
 * same as no list — the venue tab learned that first.
 */

const today = (): string => new Date().toISOString().slice(0, 10);

/** Which buckets are worth seeing without being asked. */
const OPEN_BY_DEFAULT: readonly TaskBucket[] = ['overdue', 'now'];

export function TimelineTab() {
  const { t } = useTranslation();
  const { wedding, canWrite } = useOutletContext<{ wedding: Wedding; canWrite: boolean }>();
  const wedId = wedding.wedId;

  const tasks = useTasks(wedId);
  const create = useCreateTask(wedId);
  const update = useUpdateTask(wedId);
  const remove = useDeleteTask(wedId);
  const generate = useGenerateChecklist(wedId);
  const reschedule = useRescheduleChecklist(wedId);

  const [categoryFilter, setCategoryFilter] = useState<TaskCategory | 'all'>('all');
  const [newTitle, setNewTitle] = useState('');
  const [newDue, setNewDue] = useState('');
  const [closed, setClosed] = useState<Set<TaskBucket>>(new Set());

  const all = tasks.data?.items ?? [];
  const now = today();
  const stats = tasks.data?.stats ?? taskStats(all, now);
  const staleCount = tasks.data?.staleCount ?? 0;

  const shown = useMemo(
    () => all.filter((task) => categoryFilter === 'all' || task.category === categoryFilter),
    [all, categoryFilter],
  );
  const groups = useMemo(() => groupTasks(shown, now), [shown, now]);

  // Only the categories in play, so the filter row is not twelve pills of which
  // nine match nothing.
  const usedCategories = useMemo(
    () => TASK_CATEGORIES.filter((c) => all.some((task) => task.category === c)),
    [all],
  );

  const isOpen = (bucket: TaskBucket) =>
    OPEN_BY_DEFAULT.includes(bucket) ? !closed.has(bucket) : closed.has(bucket);

  const toggleBucket = (bucket: TaskBucket) =>
    setClosed((prev) => {
      const next = new Set(prev);
      if (next.has(bucket)) next.delete(bucket);
      else next.add(bucket);
      return next;
    });

  const toggleDone = (task: Task) =>
    update.mutate({ taskId: task.taskId, input: { done: !task.done, updatedAt: task.updatedAt } });

  const onAdd = (e: FormEvent) => {
    e.preventDefault();
    const title = newTitle.trim();
    if (!title) return;
    create.mutate({ title, ...(newDue ? { dueDate: newDue } : {}) });
    setNewTitle('');
    setNewDue('');
  };

  const done = stats.done;
  const remaining = stats.total - done;

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-4">
        <h1 className="text-page">{t('nav.timeline')}</h1>

        <div className="flex flex-wrap items-end gap-8">
          <Metric
            label={t('tasks.remaining')}
            value={remaining}
            sub={t('tasks.ofTotal', { total: stats.total })}
          />
          <Metric
            label={t('tasks.overdue')}
            value={stats.overdue}
            sub={stats.overdue > 0 ? t('tasks.overdueHint') : t('tasks.nothingLate')}
          />
        </div>
        <MetricFootnote
          items={[
            t('tasks.doneCount', { count: done }),
            stats.dueSoon > 0 && t('tasks.dueSoonCount', { count: stats.dueSoon }),
            stats.undated > 0 && t('tasks.undatedCount', { count: stats.undated }),
          ]}
        />
        <WeeklyReminderToggle />
      </header>

      {/* The dates no longer match the date. Said plainly, with the fix next to
          it, rather than silently moving tasks somebody may have moved on
          purpose. */}
      {staleCount > 0 && canWrite && (
        <div className="panel flex flex-wrap items-center justify-between gap-3 p-4" role="status">
          <p className="text-body">{t('tasks.staleBanner', { count: staleCount })}</p>
          <Button
            variant="secondary"
            loading={reschedule.isPending}
            onClick={() => reschedule.mutate()}
          >
            {t('tasks.rescheduleAction')}
          </Button>
        </div>
      )}

      {canWrite && (
        <form className="flex flex-wrap items-center gap-2" onSubmit={onAdd}>
          <label className="min-w-[14rem] flex-1">
            <span className="sr-only">{t('tasks.newTitle')}</span>
            <TextInput
              placeholder={t('tasks.newTitle')}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
            />
          </label>
          <label>
            <span className="sr-only">{t('tasks.newDue')}</span>
            <TextInput type="date" value={newDue} onChange={(e) => setNewDue(e.target.value)} />
          </label>
          <Button type="submit" variant="secondary" loading={create.isPending}>
            {t('tasks.add')}
          </Button>
        </form>
      )}

      {usedCategories.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <FilterPill
            label={t('tasks.allCategories')}
            selected={categoryFilter === 'all'}
            onSelect={() => setCategoryFilter('all')}
          />
          {usedCategories.map((category) => (
            <FilterPill
              key={category}
              label={t(`taskCategories.${category}`)}
              count={all.filter((task) => task.category === category).length}
              selected={categoryFilter === category}
              onSelect={() => setCategoryFilter(category)}
            />
          ))}
        </div>
      )}

      {all.length === 0 ? (
        <EmptyState
          title={t('tasks.emptyTitle')}
          body={wedding.date ? t('tasks.emptyBody') : t('tasks.emptyNoDate')}
          action={
            wedding.date && canWrite ? (
              <Button loading={generate.isPending} onClick={() => generate.mutate()}>
                {t('tasks.generate')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((group) => {
            const open = isOpen(group.bucket);
            return (
              <section key={group.bucket} className="flex flex-col">
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => toggleBucket(group.bucket)}
                  className="flex min-h-tap items-center gap-3 border-b border-line text-left"
                >
                  <span aria-hidden="true" className="text-leaf-soft">
                    {open ? '−' : '+'}
                  </span>
                  <span className="eyebrow">{t(`taskBuckets.${group.bucket}`)}</span>
                  <span className="tabular text-caption text-leaf-soft">{group.tasks.length}</span>
                </button>

                {open && (
                  <ul className="flex flex-col divide-y divide-line">
                    {group.tasks.map((task) => (
                      <TaskRow
                        key={task.taskId}
                        task={task}
                        onToggle={() => toggleDone(task)}
                        onDelete={() => remove.mutate({ taskId: task.taskId })}
                      />
                    ))}
                  </ul>
                )}
              </section>
            );
          })}

          {/* Topping up after the template grows, or after a date was added
              later. Worded as "add the ones you are missing" because that is
              exactly what it does — it never overwrites or restores. */}
          {wedding.date && (
            <div className="flex justify-start pt-2">
              <Button
                variant="secondary"
                loading={generate.isPending}
                onClick={() => generate.mutate()}
              >
                {t('tasks.topUp')}
              </Button>
            </div>
          )}
        </div>
      )}

      <Suggestions wedId={wedId} hasDate={Boolean(wedding.date)} />
    </section>
  );
}

function TaskRow({
  task,
  onToggle,
  onDelete,
}: {
  task: Task;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const overdue = !task.done && task.dueDate !== undefined && task.dueDate < today();

  return (
    <li className="flex items-center gap-3 py-2">
      {/* A real checkbox: it is the only control on the row that matters, and a
          styled div would lose the keyboard and the screen reader with it. */}
      <input
        type="checkbox"
        checked={task.done}
        onChange={onToggle}
        className="size-5 shrink-0 accent-leaf"
        aria-label={task.title}
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={`truncate text-body ${task.done ? 'text-leaf-soft line-through' : ''}`}>
          {task.title}
        </span>
        <span className="flex flex-wrap gap-x-2 text-caption text-leaf-soft">
          <span>{t(`taskCategories.${task.category}`)}</span>
          {task.dueDate && (
            <span className={`tabular ${overdue ? 'text-status-declinedFg' : ''}`}>
              {task.dueDate}
            </span>
          )}
        </span>
      </span>
      <button
        type="button"
        onClick={onDelete}
        className="min-h-tap shrink-0 px-2 text-caption text-leaf-soft underline-offset-4 hover:text-status-declinedFg hover:underline"
      >
        {t('common.remove')}
      </button>
    </li>
  );
}
